import { CheckCircle2, Clock, AlertCircle, TrendingUp, Calendar } from 'lucide-react';

function StatCard({ icon: Icon, label, value, subtext, color = 'text-accent', bgColor = 'bg-accent/10' }) {
  return (
    <div className="bg-surface-card rounded-xl border border-border p-4">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
          <Icon size={20} className={color} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold text-text-primary">{value}</p>
          <p className="text-sm text-text-secondary">{label}</p>
          {subtext && <p className="text-xs text-text-tertiary mt-1">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

function TaskList({ title, tasks, emptyMessage, icon: Icon, iconColor }) {
  if (tasks.length === 0) {
    return (
      <div className="bg-surface-card rounded-xl border border-border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon size={16} className={iconColor} />
          <h3 className="text-sm font-medium text-text-primary">{title}</h3>
        </div>
        <p className="text-sm text-text-tertiary italic">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="bg-surface-card rounded-xl border border-border p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={iconColor} />
        <h3 className="text-sm font-medium text-text-primary">{title}</h3>
        <span className="text-xs text-text-tertiary bg-surface-hover px-2 py-0.5 rounded-full ml-auto">
          {tasks.length}
        </span>
      </div>
      <div className="space-y-2 max-h-48 overflow-auto">
        {tasks.slice(0, 10).map(task => (
          <div key={task.id} className="flex items-center gap-2 text-sm">
            <div className={`w-1.5 h-1.5 rounded-full ${iconColor.replace('text-', 'bg-')}`} />
            <span className="text-text-secondary truncate">{task.title}</span>
          </div>
        ))}
        {tasks.length > 10 && (
          <p className="text-xs text-text-tertiary">+{tasks.length - 10} more</p>
        )}
      </div>
    </div>
  );
}

export default function WeekInReview({ metrics, weekBounds }) {
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 text-text-tertiary text-sm mb-2">
            <Calendar size={14} />
            <span>{formatDate(weekBounds.start)} - {formatDate(weekBounds.end)}</span>
          </div>
          <h2 className="text-xl font-semibold text-text-primary">Your Week at a Glance</h2>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard
            icon={CheckCircle2}
            label="Tasks Completed"
            value={metrics.tasksCompletedCount}
            color="text-success"
            bgColor="bg-success/10"
          />
          <StatCard
            icon={Clock}
            label="Carried Over"
            value={metrics.tasksCarriedOverCount}
            subtext="Not completed yet"
            color="text-warning"
            bgColor="bg-warning/10"
          />
          <StatCard
            icon={TrendingUp}
            label="Habits Rate"
            value={`${metrics.habitsCompletionRate}%`}
            subtext={`${metrics.weeklyHabitsChecked}/${metrics.weeklyHabitsTotal} weekly`}
            color="text-weekly"
            bgColor="bg-weekly/10"
          />
          <StatCard
            icon={AlertCircle}
            label="Waiting On"
            value={metrics.waitingForItems.length}
            subtext="Items pending"
            color="text-text-tertiary"
            bgColor="bg-surface-hover"
          />
        </div>

        {/* Task lists */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TaskList
            title="Completed This Week"
            tasks={metrics.tasksCompletedList}
            emptyMessage="No tasks completed yet"
            icon={CheckCircle2}
            iconColor="text-success"
          />
          <TaskList
            title="Carried Over"
            tasks={metrics.tasksCarriedOverList}
            emptyMessage="Nothing carried over"
            icon={Clock}
            iconColor="text-warning"
          />
        </div>

        {/* Project summary */}
        <div className="mt-6 bg-surface-card rounded-xl border border-border p-4">
          <h3 className="text-sm font-medium text-text-primary mb-3">Project Health</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-2xl font-bold text-text-primary">{metrics.activeProjects.length}</p>
              <p className="text-sm text-text-secondary">Active Projects</p>
            </div>
            {metrics.projectsWithoutNextActions.length > 0 && (
              <div className="flex-1 p-3 bg-warning/10 rounded-lg">
                <p className="text-lg font-bold text-warning">{metrics.projectsWithoutNextActions.length}</p>
                <p className="text-xs text-warning">Need Next Actions</p>
              </div>
            )}
          </div>
        </div>

        {/* Encouragement */}
        <div className="mt-6 text-center">
          {metrics.tasksCompletedCount >= 10 ? (
            <p className="text-sm text-success">Great week! You completed {metrics.tasksCompletedCount} tasks.</p>
          ) : metrics.tasksCompletedCount >= 5 ? (
            <p className="text-sm text-text-secondary">Solid progress this week. Keep it up!</p>
          ) : (
            <p className="text-sm text-text-tertiary">Every completed task is progress. Let's review what's next.</p>
          )}
        </div>
      </div>
    </div>
  );
}
